import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import {
  Breadcrumb,
  BreadcrumbItem,
  Button,
  Content,
  EmptyState,
  EmptyStateActions,
  EmptyStateBody,
  EmptyStateFooter,
  Flex,
  FlexItem,
  Icon,
  PageBreadcrumb,
  PageSection,
  PageSectionVariants,
  Tab,
  Tabs,
  TabTitleText,
} from '@patternfly/react-core';
import { ClusterIcon, ExclamationCircleIcon } from '@patternfly/react-icons';
import CenteredSpinner from '@app/CenteredSpinner';
import Unauthenticated from '@app/Unauthenticated';
import { useServer } from '../../../hooks/useServer';
import renderStatusLabel from './serverStatusLabel';
import ServerPowerActions from './ServerPowerActions';
import ServerOverviewTab from './ServerOverviewTab';
import ServerHardwareTab from './ServerHardwareTab';
import ServerNetworkTab from './ServerNetworkTab';
import ServerEmailTab from './ServerEmailTab';

function ServerDetail() {
  const { serverId } = useParams<{ serverId: string }>();
  const auth = useAuth();
  const { server, loading, error, refetch } = useServer(serverId!);
  const [activeTabKey, setActiveTabKey] = useState<string | number>(0);

  if (!auth.isAuthenticated) {
    return <Unauthenticated />;
  }

  const handleTabClick = (
    _event: React.MouseEvent<HTMLElement, MouseEvent> | React.KeyboardEvent | MouseEvent,
    tabIndex: string | number,
  ) => {
    setActiveTabKey(tabIndex);
  };

  if (loading) {
    return (
      <>
        <PageBreadcrumb hasBodyWrapper={false} hasShadowBottom={false}>
          <Breadcrumb ouiaId="BasicBreadcrumb">
            <BreadcrumbItem to="/datacenter">Datacenter</BreadcrumbItem>
            <BreadcrumbItem to="/datacenter/servers">Servers</BreadcrumbItem>
            <BreadcrumbItem isActive>...</BreadcrumbItem>
          </Breadcrumb>
        </PageBreadcrumb>
        <PageSection hasBodyWrapper variant={PageSectionVariants.default} isWidthLimited hasShadowBottom={true}>
          <Content component="h1">
            <Icon size="lg" style={{ marginRight: '8px' }}>
              <ClusterIcon />
            </Icon>
            Server
          </Content>
        </PageSection>
        <PageSection hasBodyWrapper={false}>
          <CenteredSpinner />
        </PageSection>
      </>
    );
  }

  if (error || !server) {
    return (
      <>
        <PageBreadcrumb hasBodyWrapper={false} hasShadowBottom={false}>
          <Breadcrumb ouiaId="BasicBreadcrumb">
            <BreadcrumbItem to="/datacenter">Datacenter</BreadcrumbItem>
            <BreadcrumbItem to="/datacenter/servers">Servers</BreadcrumbItem>
            <BreadcrumbItem isActive>Not found</BreadcrumbItem>
          </Breadcrumb>
        </PageBreadcrumb>
        <PageSection hasBodyWrapper variant={PageSectionVariants.default} isWidthLimited hasShadowBottom={true}>
          <Content component="h1">
            <Icon size="lg" style={{ marginRight: '8px' }}>
              <ClusterIcon />
            </Icon>
            Server
          </Content>
        </PageSection>
        <PageSection hasBodyWrapper={false}>
          <EmptyState
            titleText="Unable to load server"
            icon={ExclamationCircleIcon}
            variant="lg"
            headingLevel="h2"
            status="danger"
          >
            <EmptyStateBody>{error ? error.message : 'Server not found.'}</EmptyStateBody>
            <EmptyStateFooter>
              <EmptyStateActions>
                <Button variant="primary" onClick={() => refetch()}>
                  Retry
                </Button>
              </EmptyStateActions>
            </EmptyStateFooter>
          </EmptyState>
        </PageSection>
      </>
    );
  }

  return (
    <>
      <PageBreadcrumb hasBodyWrapper={false} hasShadowBottom={false}>
        <Breadcrumb ouiaId="BasicBreadcrumb">
          <BreadcrumbItem to="/datacenter">Datacenter</BreadcrumbItem>
          <BreadcrumbItem to="/datacenter/servers">Servers</BreadcrumbItem>
          <BreadcrumbItem isActive>{server.name}</BreadcrumbItem>
        </Breadcrumb>
      </PageBreadcrumb>
      <PageSection hasBodyWrapper variant={PageSectionVariants.default} isWidthLimited hasShadowBottom={true}>
        <Flex justifyContent={{ default: 'justifyContentSpaceBetween' }} alignItems={{ default: 'alignItemsCenter' }}>
          <FlexItem>
            <Content component="h1">
              <Icon size="lg" style={{ marginRight: '8px' }}>
                <ClusterIcon />
              </Icon>
              {server.name}
            </Content>
            <Content component="p">{renderStatusLabel(server.status)} Server details and management</Content>
          </FlexItem>
          <FlexItem>
            <ServerPowerActions server={server} />
          </FlexItem>
        </Flex>
      </PageSection>
      <PageSection hasBodyWrapper={false}>
        <Tabs activeKey={activeTabKey} onSelect={handleTabClick}>
          <Tab eventKey={0} title={<TabTitleText>Overview</TabTitleText>}>
            <ServerOverviewTab server={server} onServerUpdate={refetch} />
          </Tab>
          <Tab eventKey={1} title={<TabTitleText>Hardware</TabTitleText>}>
            <ServerHardwareTab />
          </Tab>
          <Tab eventKey={2} title={<TabTitleText>Network</TabTitleText>}>
            <ServerNetworkTab serverId={server.id!} />
          </Tab>
          {server.is_imap && (
            <Tab eventKey={3} title={<TabTitleText>Email</TabTitleText>}>
              <ServerEmailTab />
            </Tab>
          )}
        </Tabs>
      </PageSection>
    </>
  );
}

export default ServerDetail;
